import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { ComplianceTasksTableComponent } from '../../../../../../../_shared/components/compliance-tasks/compliance-tasks-table.component';
import { TableColumn } from '../../../../../../../compliance-overview/models/compliance-item.model';
import {
  AssignedAssetComplianceHistory,
  AssignedAssetComplianceHistoryRequest
} from '../../../../../../models/compliances.model';
import { CompliancesLibraryService } from '../../../../../../services/compliances-library.service';
import { ErrorHandlerService } from '../../../../../../../../../_shared/services/error-handler.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

type AssignedAssetComplianceHistoryRow = AssignedAssetComplianceHistory & {
  date: Date;
  dateLabel: string;
  documentCountLabel: string;
  failedChecklistCategoriesLabel: string;
  statusLabel: string;
  inspectorName: string;
};

@Component({
  selector: 'app-asset-history',
  standalone: true,
  imports: [
    RouterModule,
    ComplianceTasksTableComponent,
    TranslocoDirective
  ],
  templateUrl: './asset-history.component.html',
  styleUrl: './asset-history.component.scss'
})
export class AssetHistoryComponent implements OnInit, OnDestroy {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private complianceLibraryService: CompliancesLibraryService = inject(CompliancesLibraryService);
  private errorService: ErrorHandlerService = inject(ErrorHandlerService);
  private transloco: TranslocoService = inject(TranslocoService);
  private readonly cancelSubject = new Subject<void>();

  assetId: number | null = null;
  complianceId: number | null = null;
  assetName: string = '';
  complianceName: string = '';
  complianceType: string = '';

  isLoading: boolean = true;
  isLoadingMore: boolean = false;
  hasError: boolean = false;
  currentPage: number = 1;
  pageSize: number = 20;
  totalPages: number = 0;
  totalCount: number = 0;
  historyRows: AssignedAssetComplianceHistoryRow[] = [];
  columns: TableColumn[] = [];

  ngOnInit(): void {
    this.assetId = this.getNumberQueryParam('assetId');
    this.complianceId = this.getNumberQueryParam('complianceId');
    this.assetName = this.route.snapshot.queryParamMap.get('assetName') || '';
    this.complianceName = this.route.snapshot.queryParamMap.get('complianceName') || '';
    this.complianceType = this.route.snapshot.queryParamMap.get('complianceType') || '';
    this.columns = this.buildColumns();

    if (!this.assetId || !this.complianceId) {
      this.hasError = true;
      this.isLoading = false;
      return;
    }

    this.resetAndLoad();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async loadHistoryAsync(resetResults: boolean = false): Promise<void> {
    if (!this.assetId || !this.complianceId) {
      return;
    }

    this.hasError = false;
    const requestedPage = this.currentPage;
    const request = this.buildRequest(requestedPage);

    try {
      if (requestedPage === 1 || resetResults) {
        this.isLoading = true;
        this.isLoadingMore = false;
        this.cancelSubject.next();
      } else {
        this.isLoadingMore = true;
      }

      const response = await this.complianceLibraryService.getAssignedAssetComplianceHistoryAsync(request, this.cancelSubject);
      const result = response.data;
      const rows = (result?.results ?? []).map(item => this.mapHistory(item));

      if (requestedPage === 1) {
        this.historyRows = rows;
      } else {
        this.historyRows = [...this.historyRows, ...rows];
      }

      this.totalCount = result?.totalCount ?? 0;
      this.totalPages = result?.totalPages ?? 0;
      this.isLoading = false;
      this.isLoadingMore = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          if (requestedPage > 1) {
            this.currentPage--;
          }

          this.hasError = true;
          this.isLoading = false;
          this.isLoadingMore = false;
        }
      });
    }
  }

  onScrollDown(): void {
    if (this.isLoading || this.isLoadingMore || this.currentPage >= this.totalPages) {
      return;
    }

    this.currentPage++;
    void this.loadHistoryAsync();
  }

  onRowClick(item: AssignedAssetComplianceHistoryRow): void {
    void this.router.navigate(
      ['/asset-management/compliance-library/compliance-details/assigned-assets/history/checklist-results'],
      {
        queryParams: {
          ...this.route.snapshot.queryParams,
          taskId: item.taskId
        }
      }
    );
  }

  private buildColumns(): TableColumn[] {
    const typeColumnHeader = this.complianceType.toLowerCase() === 'routine'
      ? this.transloco.translate('compliance.library.assetHistory.columns.routineType')
      : this.transloco.translate('compliance.library.assetHistory.columns.frequency');

    return [
      { header: this.transloco.translate('compliance.library.assetHistory.columns.date'), value: 'dateLabel', width: 'col', type: 'text' },
      { header: typeColumnHeader, value: 'type', width: 'col', type: 'text' },
      { header: this.transloco.translate('compliance.library.assetHistory.columns.documents'), value: 'documentCountLabel', width: 'col', type: 'text' },
      { header: this.transloco.translate('compliance.library.assetHistory.columns.failed'), value: 'failedChecklistCategoriesLabel', width: 'col', type: 'text' },
      { header: this.transloco.translate('compliance.library.assetHistory.columns.inspectedBy'), value: 'inspectorName', width: 'col', type: 'text' },
      { header: this.transloco.translate('compliance.library.assetHistory.columns.status'), value: 'statusLabel', width: 'col', type: 'status' }
    ];
  }

  private buildRequest(page: number): AssignedAssetComplianceHistoryRequest {
    return {
      assetId: this.assetId!,
      complianceId: this.complianceId!,
      page,
      pageSize: this.pageSize
    };
  }

  private resetAndLoad(): void {
    this.currentPage = 1;
    this.totalPages = 0;
    this.totalCount = 0;
    this.historyRows = [];
    void this.loadHistoryAsync(true);
  }

  private mapHistory(item: AssignedAssetComplianceHistory): AssignedAssetComplianceHistoryRow {
    const date = new Date(item.date);

    return {
      ...item,
      date,
      dateLabel: this.formatDate(date),
      documentCountLabel: this.getDocumentCountLabel(item.documentCount),
      failedChecklistCategoriesLabel: item.failedChecklistCategories?.trim() || '-',
      statusLabel: item.isCompliant ? 'Compliant' : 'Non-Compliant',
      inspectorName: item.inspectorName
    };
  }

  private getDocumentCountLabel(documentCount: number): string {
    if (documentCount === 0) {
      return this.transloco.translate('compliance.library.assetHistory.noDocuments');
    }

    return this.transloco.translate(
      documentCount === 1
        ? 'compliance.library.assetHistory.documentCountOne'
        : 'compliance.library.assetHistory.documentCountMany',
      { count: documentCount }
    );
  }

  private formatDate(date: Date): string {
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  }

  private getNumberQueryParam(key: string): number | null {
    const value = Number(this.route.snapshot.queryParamMap.get(key));
    return Number.isFinite(value) && value > 0 ? value : null;
  }
}